import React from 'react';
import { Eye, EyeOff, Trash2, Download, ShieldAlert } from 'lucide-react';

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

// The compacted summary is free-form LLM text, but a session driven by
// `/goal` consistently opens with a "## Goal" section — pull just that out
// so the card reads as "what was this session for", not a markdown dump.
// Falls back to the full summary when the marker isn't present.
function extractGoal(summary) {
  if (!summary) return '';
  const match = summary.match(/##\s*goal\s*[-–:]?\s*([\s\S]*?)(?=##|$)/i);
  const text = match && match[1] ? match[1].trim() : summary.trim();
  return text || summary.trim();
}

function IconButton({ icon: Icon, title, onClick, disabled, danger }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className={`p-2 rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
        danger
          ? 'border-panel-border text-red-400 hover:bg-red-500/10 hover:border-red-500/40'
          : 'border-panel-border text-fg-muted hover:text-fg hover:border-fg-muted'
      }`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}

/** One shared-session card — used by both the personal "Shared Sessions"
 *  page (own shares only) and the admin "Session Registry" page (org-wide
 *  moderation). `canModerate` gates hide/delete on a share the viewer
 *  doesn't own; `s.is_own` always allows it regardless. `showOwnBadge`
 *  defaults on for the mixed-ownership registry view; the personal page
 *  (every card is always the viewer's own) turns it off as redundant. */
export default function SharedSessionCard({ s, onHide, onDelete, onDownload, busy, canModerate, showOwnBadge = true }) {
  const goal = extractGoal(s.summary);
  const uploaderLabel = s.uploader_name || s.uploader_email || 'Unknown';
  const canManage = s.is_own || canModerate;

  return (
    <div className="bg-panel border border-panel-border rounded-xl p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center flex-wrap gap-2">
            <h3 className="text-fg font-semibold text-base leading-snug">{s.title || 'Untitled session'}</h3>
            {s.hidden_by_admin && (
              // `hidden_by_admin` alone doesn't say who hid it — self-hide
              // vs. moderation get distinct badges.
              s.hidden_by_uploader ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-fg/10 text-fg-muted shrink-0">
                  <EyeOff className="w-3 h-3" /> Hidden by uploader
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 shrink-0">
                  <ShieldAlert className="w-3 h-3" /> Hidden by admin
                </span>
              )
            )}
            {showOwnBadge && s.is_own && (
              <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-orange/15 text-orange shrink-0">
                You
              </span>
            )}
          </div>

          {goal && (
            <p className="text-sm text-fg-muted leading-relaxed mt-2 line-clamp-2">{goal}</p>
          )}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {/* Download hidden from the UI — uncomment to restore. */}
          {/* <IconButton icon={Download} title="Download full JSON" onClick={() => onDownload(s)} disabled={busy} /> */}
          {canManage && (
            <>
              <IconButton
                icon={s.hidden_by_admin ? Eye : EyeOff}
                title={s.hidden_by_admin ? 'Unhide' : 'Hide'}
                onClick={() => onHide(s)}
                disabled={busy}
              />
              <IconButton icon={Trash2} title="Delete" onClick={() => onDelete(s)} disabled={busy} danger />
            </>
          )}
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between text-xs">
        <div><span className="text-sm font-semibold text-fg">Shared by:</span> <span className="text-fg-muted">{uploaderLabel}</span></div>
        <div className="text-[11px] text-fg-muted">Updated {fmtDate(s.updated_at)}</div>
      </div>
    </div>
  );
}
